<template>
  <form>
    <gg-select outlined
              v-model="commonSelect"
              :items="commonSelectOptions"
              item-disabled="disabled"
              label="Common options"
              @change="applyCronOptions($event)"></gg-select>
    <v-row>
      <!-- minutes -->
      <v-col cols="12" sm="4">
        <gg-text-field outlined
                      v-model.trim="cron.minute"
                      label="Minute"
                      name="Minute"
                      v-validate="{ cron_minutes: true }"
                      :error-messages="errors.collect('Minute')"></gg-text-field>
      </v-col>
      <v-col cols="12" sm="8">
        <gg-select outlined
                  v-model="minuteSelect"
                  label="Minute Options"
                  :items="minuteSelectOptions"
                  @change="applyCronOption('minute', $event)"></gg-select>
      </v-col>
      <!-- hours -->
      <v-col cols="12" sm="4">
        <gg-text-field outlined
                      v-model.trim="cron.hour"
                      label="Hour"
                      name="Hour"
                      v-validate="{ cron_hours: true }"
                      :error-messages="errors.collect('Hour')"></gg-text-field>
      </v-col>
      <v-col cols="12" sm="8">
        <gg-select outlined
                  v-model="hourSelect"
                  label="Hour Options"
                  :items="hourSelectOptions"
                  @change="applyCronOption('hour', $event)"></gg-select>
      </v-col>
      <!-- days -->
      <v-col cols="12" sm="4">
        <gg-text-field outlined
                      v-model.trim="cron.day"
                      label="Day"
                      name="Day"
                      v-validate="{ cron_days: true }"
                      :error-messages="errors.collect('Day')"></gg-text-field>
      </v-col>
      <v-col cols="12" sm="8">
        <gg-select outlined
                  v-model="daySelect"
                  label="Day Options"
                  :items="daySelectOptions"
                  @change="applyCronOption('day', $event)"></gg-select>
      </v-col>
      <!-- months -->
      <v-col cols="12" sm="4">
        <gg-text-field outlined
                      v-model.trim="cron.month"
                      label="Month"
                      name="Month"
                      v-validate="{ cron_months: true }"
                      :error-messages="errors.collect('Month')"></gg-text-field>
      </v-col>
      <v-col cols="12" sm="8">
        <gg-select outlined
                  v-model="monthSelect"
                  label="Month Options"
                  :items="monthSelectOptions"
                  @change="applyCronOption('month', $event)"></gg-select>
      </v-col>
      <!-- weekdays -->
      <v-col cols="12" sm="4">
        <gg-text-field outlined
                      v-model.trim="cron.weekday"
                      label="Weekday"
                      name="Weekday"
                      v-validate="{ cron_weekdays: true }"
                      :error-messages="errors.collect('Weekday')"></gg-text-field>
      </v-col>
      <v-col cols="12" sm="8">
        <gg-select outlined
                  v-model="weekdaySelect"
                  label="Weekday Options"
                  :items="weekdaySelectOptions"
                  @change="applyCronOption('weekday', $event)"></gg-select>
      </v-col>
    </v-row>
    <gg-text-field outlined label="Result Cron" :value="cronString" readonly></gg-text-field>
    <gg-text-field outlined
                  v-model="command"
                  label="Command"
                  name="Command"
                  v-validate="'required'"
                  :error-messages="errors.collect('Command')"></gg-text-field>
    <template v-if="actions">
      <gg-btn color="green"
             class="ml-0"
             tile
             :loading="loading"
             @click="submit"
             dark>Add New Cron Job</gg-btn>
    </template>
  </form>
</template>

<script>
import { mapActions } from 'vuex'
import { Validator } from 'vee-validate'
import {
  presetsRange,
  weekdaysRange,
  monthsRange,
  daysRange,
  hoursRange,
  minutesRange,
  parseCronString,
  validateCronString,
} from '@/helpers/cron'
import {
  cronMinutesValidator,
  cronHoursValidator,
  cronDaysValidator,
  cronWeekdaysValidator,
  cronMonthsValidator,
} from '@/helpers/validators'

export default {
  name: 'CronJobForm',
  props: {
    beginningCron: {
      required: false,
      type: Object,
      default: null,
    },
    // if lineKey will be specified - component will work in 'Editing' mode
    lineKey: {
      required: false,
      default: undefined,
      type: [String, Number],
    },
    actions: {
      required: false,
      default: true,
      type: Boolean,
    },
  },
  data () {
    return {
      command: '',
      cron: {
        minute: '*',
        hour: '*',
        day: '*',
        month: '*',
        weekday: '*',
      },
      // minutes
      minuteSelect: '*',
      minuteSelectOptions: [],
      // hours
      hourSelect: '*',
      hourSelectOptions: [],
      // days
      daySelect: '*',
      daySelectOptions: [],
      // months
      monthSelect: '*',
      monthSelectOptions: [],
      // weekdays
      weekdaySelect: '*',
      weekdaySelectOptions: [],
      // common options
      commonSelect: '* * * * *',
      commonSelectOptions: [],
      loading: false,
    }
  },
  computed: {
    cronString () {
      const str = `${this.cron.minute} ${this.cron.hour} ${this.cron.day} ${this.cron.month} ${this.cron.weekday}`.trim()
      return validateCronString(str) ? str : ''
    },
    canSubmit () {
      // TODO: refactor
      const invalidFields = Object.keys(this.fields).filter(key => this.fields[key].invalid)
      return !this.isLoading && invalidFields.length === 0
    },
  },
  methods: {
    ...mapActions(['addCronLine', 'editCronLine', 'reportSuccess', 'reportError']),
    applyCronOption (optName, value) {
      if (Object.keys(this.cron).indexOf(optName) === -1) {
        // invalid optName
        return
      }
      this.cron[optName] = value
    },
    applyCronOptions (value) {
      if (validateCronString(value)) {
        this.cron = parseCronString(value)
      }
    },
    submit () {
      this.$validator.validateAll().then(valid => {
        if (!valid) {
          return
        }
        this.loading = true
        this.$emit('start-loading')
        if (typeof this.lineKey !== 'undefined') {
          // Editing mode
          this.editCronLine({
            ...this.cron,
            command: this.command,
            linekey: this.lineKey,
            _username: this.$route.query.user,
          }).then(() => {
            this.loading = false
            this.$emit('end-loading')
            this.reportSuccess(`Cron job successfully updated.`)
            this.reset()
            this.$emit('job-updated')
          }).catch(error => {
            this.loading = false
            this.reportError(error)
          })
        } else {
          // Creating mode
          this.addCronLine({
            ...this.cron,
            command: this.command,
            _username: this.$route.query.user,
          }).then(() => {
            this.loading = false
            this.$emit('end-loading')
            this.reportSuccess(`New cron job successfully added.`)
            this.reset()
            this.$emit('job-added')
          }).catch((error) => {
            this.loading = false
            this.$emit('end-loading')
            this.reportError(error)
          })
        }
      })
    },
    reset () {
      if (this.beginningCron) {
        this.command = this.beginningCron.command
        this.cron = { ...this.beginningCron.cron }
      } else {
        this.cron = {
          minute: '*',
          hour: '*',
          day: '*',
          month: '*',
          weekday: '*',
        }
        this.command = ''
      }
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },
  },
  watch: {
    cronString (value) {
      // apply this.cron values on each select
      Object.entries(this.cron).forEach(entire => {
        const select = `${entire[0]}Select`
        this[select] = this[`${select}Options`].findIndex(option => option.value === entire[1]) !== -1
          ? entire[1]
          : ''
        // '' as 'Common settings' option
      })
      // apply value on Common options select
      this.commonSelect = this.commonSelectOptions.findIndex(option => option.value === value) !== -1 ? value : ''
    },
  },
  created () {
    // initialize options arrays
    this.minuteSelectOptions = minutesRange()
    this.hourSelectOptions = hoursRange()
    this.daySelectOptions = daysRange()
    this.monthSelectOptions = monthsRange()
    this.weekdaySelectOptions = weekdaysRange()
    this.commonSelectOptions = presetsRange
    // setup validators
    Validator.extend('cron_minutes', cronMinutesValidator)
    Validator.extend('cron_hours', cronHoursValidator)
    Validator.extend('cron_days', cronDaysValidator)
    Validator.extend('cron_months', cronMonthsValidator)
    Validator.extend('cron_weekdays', cronWeekdaysValidator)
    // initialise beginning values
    if (this.beginningCron) {
      this.cron = { ...this.beginningCron.job }
      this.command = this.beginningCron.command
    }
  }
}
</script>
